import axios from 'axios'
import moment from 'moment'
import 'moment/locale/es'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { constants } from '../Constants'
import { Badge } from '../components/ui'
import { PageLoader } from '../components/ui/Spinner/Spinner'
import './MyCalendar.scss'

moment.locale('es')

const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']

// Clave de día en UTC (los eventos se guardan como hora de pared en UTC y el
// resto de la app muestra la hora con .utc(), así que el calendario también)
const dayKey = date => moment(date).utc().format('YYYY-MM-DD')

export const MyCalendar = () => {
  const user = JSON.parse(localStorage.getItem('user'))
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentMonth, setCurrentMonth] = useState(
    moment.utc().startOf('month')
  )
  const [selectedDate, setSelectedDate] = useState(
    moment.utc().format('YYYY-MM-DD')
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${constants.urlLocal}inscription/user/${user.id}`
        )
        const list = (res.data.inscriptions || [])
          .filter(i => i.eventId && i.eventId.start && i.eventId.end)
          .map(i => {
            const start = i.eventId.start
            const end = i.eventId.end
            return {
              id: i.eventId._id,
              inscriptionId: i._id,
              title: i.eventId.title,
              color: i.eventId.color || '#2440ab',
              difficulty: i.eventId.difficulty || null,
              start,
              end,
              attended: moment(end).isBefore(moment()),
            }
          })
          .sort((a, b) => moment(a.start).diff(moment(b.start)))
        setClasses(list)
      } catch (error) {
        console.error('Error al obtener el calendario', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Agrupar clases por día (clave UTC)
  const classesByDay = useMemo(() => {
    const map = {}
    classes.forEach(c => {
      const key = dayKey(c.start)
      if (!map[key]) map[key] = []
      map[key].push(c)
    })
    return map
  }, [classes])

  // Estadísticas
  const stats = useMemo(() => {
    const attended = classes.filter(c => c.attended)
    const upcoming = classes.filter(c => !c.attended)
    const nowMonthKey = moment.utc().format('YYYY-MM')
    const thisMonth = attended.filter(
      c => moment(c.start).utc().format('YYYY-MM') === nowMonthKey
    ).length

    const totalHours = attended.reduce(
      (sum, c) => sum + moment(c.end).diff(moment(c.start), 'minutes') / 60,
      0
    )

    // Desglose por categoría (título = nombre de categoría)
    const byCat = {}
    attended.forEach(c => {
      if (!byCat[c.title]) byCat[c.title] = { count: 0, color: c.color }
      byCat[c.title].count++
    })
    const categories = Object.entries(byCat)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.count - a.count)

    // Racha de semanas consecutivas con al menos una clase asistida
    const attendedWeeks = new Set(
      attended.map(c => moment(c.start).utc().format('GGGG-WW'))
    )
    let streak = 0
    const wk = moment.utc().startOf('isoWeek')
    if (!attendedWeeks.has(wk.format('GGGG-WW'))) wk.subtract(1, 'week')
    while (attendedWeeks.has(wk.format('GGGG-WW'))) {
      streak++
      wk.subtract(1, 'week')
    }

    return {
      attended: attended.length,
      upcoming: upcoming.length,
      thisMonth,
      totalHours: Math.round(totalHours),
      categories,
      streak,
      favorite: categories[0]?.name || '—',
    }
  }, [classes])

  // Días del mes (rejilla completa de lunes a domingo)
  const calendarDays = useMemo(() => {
    const start = moment(currentMonth).startOf('month').startOf('isoWeek')
    const end = moment(currentMonth).endOf('month').endOf('isoWeek')
    const days = []
    const cursor = moment(start)
    while (cursor.isSameOrBefore(end, 'day')) {
      days.push(moment(cursor))
      cursor.add(1, 'day')
    }
    return days
  }, [currentMonth])

  const selectedClasses = classesByDay[selectedDate] || []
  const todayKey = moment.utc().format('YYYY-MM-DD')

  if (loading)
    return (
      <div className="loading-center">
        <PageLoader text="Cargando tu calendario..." />
      </div>
    )

  return (
    <div className="page-container mycal">
      <h2 className="page-title" style={{ marginBottom: '0.25rem' }}>
        Mi Calendario
      </h2>
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        Tus clases, asistencias y progreso
      </p>

      {/* Estadísticas */}
      <div className="mycal__stats">
        <div className="mycal__stat">
          <span className="mycal__stat-num">{stats.attended}</span>
          <span className="mycal__stat-label">Asistidas</span>
        </div>
        <div className="mycal__stat">
          <span className="mycal__stat-num">{stats.thisMonth}</span>
          <span className="mycal__stat-label">Este mes</span>
        </div>
        <div className="mycal__stat">
          <span className="mycal__stat-num">{stats.upcoming}</span>
          <span className="mycal__stat-label">Próximas</span>
        </div>
        <div className="mycal__stat">
          <span className="mycal__stat-num">{stats.totalHours}h</span>
          <span className="mycal__stat-label">Entrenadas</span>
        </div>
        <div className="mycal__stat mycal__stat--streak">
          <span className="mycal__stat-num">🔥 {stats.streak}</span>
          <span className="mycal__stat-label">Semanas de racha</span>
        </div>
      </div>

      {/* Calendario */}
      <div className="mycal__card">
        <div className="mycal__nav">
          <button
            className="mycal__nav-btn"
            onClick={() =>
              setCurrentMonth(moment(currentMonth).subtract(1, 'month'))
            }
            aria-label="Mes anterior"
          >
            ‹
          </button>
          <div className="mycal__nav-title">
            <strong>{currentMonth.format('MMMM')}</strong>{' '}
            <span>{currentMonth.format('YYYY')}</span>
          </div>
          <button
            className="mycal__nav-btn"
            onClick={() =>
              setCurrentMonth(moment(currentMonth).add(1, 'month'))
            }
            aria-label="Mes siguiente"
          >
            ›
          </button>
        </div>

        <button
          className="mycal__today-btn"
          onClick={() => {
            setCurrentMonth(moment.utc().startOf('month'))
            setSelectedDate(todayKey)
          }}
        >
          Hoy
        </button>

        <div className="mycal__weekdays">
          {WEEKDAYS.map(d => (
            <span key={d}>{d}</span>
          ))}
        </div>

        <div className="mycal__grid">
          {calendarDays.map(day => {
            const key = day.format('YYYY-MM-DD')
            const dayClasses = classesByDay[key] || []
            const isCurrentMonth = day.isSame(currentMonth, 'month')
            const isToday = key === todayKey
            const isSelected = key === selectedDate
            const allAttended =
              dayClasses.length > 0 && dayClasses.every(c => c.attended)
            return (
              <button
                key={key}
                className={[
                  'mycal__day',
                  !isCurrentMonth && 'mycal__day--muted',
                  isToday && 'mycal__day--today',
                  isSelected && 'mycal__day--selected',
                  dayClasses.length > 0 && 'mycal__day--has',
                  allAttended && 'mycal__day--attended',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setSelectedDate(key)}
              >
                <span className="mycal__day-num">{day.format('D')}</span>
                {dayClasses.length > 0 && (
                  <span className="mycal__dots">
                    {dayClasses.slice(0, 3).map((c, i) => (
                      <span
                        key={i}
                        className="mycal__dot"
                        style={{ background: c.color }}
                      />
                    ))}
                    {dayClasses.length > 3 && (
                      <span className="mycal__more">
                        +{dayClasses.length - 3}
                      </span>
                    )}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        <div className="mycal__legend">
          <span>
            <i className="mycal__lg mycal__lg--today" /> Hoy
          </span>
          <span>
            <i className="mycal__lg mycal__lg--has" /> Con clase
          </span>
          <span>
            <i className="mycal__lg mycal__lg--attended" /> Asistida
          </span>
        </div>
      </div>

      {/* Detalle del día seleccionado */}
      <div className="mycal__card">
        <h3 className="mycal__detail-title">
          {moment.utc(selectedDate).format('dddd, D [de] MMMM')}
        </h3>
        {selectedClasses.length === 0 ? (
          <p className="mycal__empty">No tienes clases este día.</p>
        ) : (
          <div className="mycal__detail-list">
            {selectedClasses.map(c => (
              <Link
                to={`/event/${c.id}`}
                key={c.inscriptionId}
                className="mycal__item"
                style={{ borderLeftColor: c.color }}
              >
                <div className="mycal__item-time">
                  <span className="mycal__item-h">
                    {moment(c.start).utc().format('HH:mm')}
                  </span>
                  <span className="mycal__item-sep">
                    {moment(c.end).utc().format('HH:mm')}
                  </span>
                </div>
                <div className="mycal__item-body">
                  <div className="mycal__item-top">
                    <span
                      className="mycal__item-dot"
                      style={{ background: c.color }}
                    />
                    <strong>{c.title}</strong>
                  </div>
                  <div className="mycal__item-meta">
                    {c.attended ? (
                      <Badge variant="success" size="sm">
                        ✓ Asistida
                      </Badge>
                    ) : moment(c.start).utc().isSame(moment.utc(), 'day') ? (
                      <Badge variant="primary" size="sm">
                        Hoy
                      </Badge>
                    ) : (
                      <Badge variant="info" size="sm">
                        Próxima
                      </Badge>
                    )}
                    {c.difficulty && (
                      <span className="mycal__item-diff">
                        Nivel: {c.difficulty}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Desglose por categoría */}
      {stats.categories.length > 0 && (
        <div className="mycal__card">
          <h3 className="mycal__detail-title">Clases asistidas por tipo</h3>
          <div className="mycal__cats">
            {stats.categories.map(cat => {
              const pct = stats.attended
                ? Math.round((cat.count / stats.attended) * 100)
                : 0
              return (
                <div key={cat.name} className="mycal__cat">
                  <div className="mycal__cat-head">
                    <span>
                      <span
                        className="mycal__item-dot"
                        style={{ background: cat.color }}
                      />
                      {cat.name}
                    </span>
                    <strong>{cat.count}</strong>
                  </div>
                  <div className="mycal__cat-bar">
                    <div
                      className="mycal__cat-fill"
                      style={{ width: `${pct}%`, background: cat.color }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default MyCalendar
